import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, Loader2, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface AuthModalProps {
  open: boolean;
  mode: 'login' | 'register';
  onClose: () => void;
  onModeChange: (mode: 'login' | 'register') => void;
}

export default function AuthModal({ open, mode, onClose, onModeChange }: AuthModalProps) {
  const { signIn, signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => { setEmail(''); setPassword(''); setUsername(''); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    if (mode === 'login') {
      const { error } = await signIn(email, password);
      if (error) toast.error(error.message ?? 'Błąd logowania');
      else { toast.success('Zalogowano!'); onClose(); reset(); }
    } else {
      if (!username.trim()) { toast.error('Podaj nazwę użytkownika'); setLoading(false); return; }
      const { error } = await signUp(email, password, username);
      if (error) toast.error(error.message ?? 'Błąd rejestracji');
      else { toast.success('Konto utworzone! Sprawdź e-mail w celu potwierdzenia.'); onClose(); reset(); }
    }
    setLoading(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
            style={{ background: 'oklch(0.05 0.01 280 / 0.85)' }}
            onClick={onClose}
          />
          {/* Modal */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            className="fixed z-50 inset-0 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto w-full max-w-md rounded-2xl border p-8 relative"
              style={{
                background: 'oklch(0.14 0.028 280)',
                borderColor: 'oklch(0.28 0.05 280)',
                boxShadow: '0 0 60px oklch(0.65 0.32 280 / 0.15)',
              }}
            >
              {/* Close */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="flex flex-col items-center mb-8">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'linear-gradient(135deg, var(--primary), var(--accent))' }}
                >
                  <Cpu className="w-6 h-6 text-white" />
                </div>
                <h2
                  className="text-2xl font-black text-foreground"
                  style={{ fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.05em' }}
                >
                  {mode === 'login' ? 'LOGOWANIE' : 'REJESTRACJA'}
                </h2>
                <p className="text-muted-foreground text-sm mt-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  {mode === 'login' ? 'Zaloguj się do swojego konta' : 'Dołącz do społeczności SOKAO-TOYS'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'register' && (
                  <div className="space-y-1.5">
                    <Label className="text-muted-foreground text-xs tracking-widest" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      NAZWA UŻYTKOWNIKA
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="TwójNick"
                        className="pl-10 bg-card border-border/60 focus:border-primary/60"
                        required
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs tracking-widest" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    E-MAIL
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="twoj@email.pl"
                      className="pl-10 bg-card border-border/60 focus:border-primary/60"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-xs tracking-widest" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    HASŁO
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={mode === 'register' ? 'Min. 6 znaków' : '••••••••'}
                      className="pl-10 bg-card border-border/60 focus:border-primary/60"
                      minLength={6}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-6 font-bold tracking-widest mt-2"
                  style={{
                    fontFamily: 'Orbitron, sans-serif',
                    fontSize: '0.8rem',
                    background: 'linear-gradient(135deg, var(--primary), var(--accent))',
                    boxShadow: '0 0 24px oklch(0.65 0.32 280 / 0.3)',
                  }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'login' ? 'ZALOGUJ SIĘ' : 'UTWÓRZ KONTO')}
                </Button>
              </form>

              {/* Toggle mode */}
              <p className="text-center text-muted-foreground text-sm mt-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                {mode === 'login' ? 'Nie masz konta?' : 'Masz już konto?'}{' '}
                <button
                  onClick={() => { onModeChange(mode === 'login' ? 'register' : 'login'); reset(); }}
                  className="text-primary hover:text-accent transition-colors font-bold"
                >
                  {mode === 'login' ? 'Zarejestruj się' : 'Zaloguj się'}
                </button>
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
